import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { VerSportsLogo } from '@versports/ui'
import { useAuth } from './AuthContext'

/**
 * Route guard. Renders `children` only when the auth context is in
 * `authenticated`. While `hydrating` (first /auth/me on boot) shows a
 * neutral placeholder so we don't flash the login page. On `anonymous`
 * redirects to /login with the current pathname stashed for return-trip.
 *
 * Una re-hidratación (el `refresh()` que corre, por ejemplo, al terminar un
 * cuestionario) también pasa por `hydrating`, pero ahí ya hay un `user`
 * conocido: en ese caso seguimos renderizando el árbol en vez de tirarlo
 * abajo. Desmontarlo hacía parpadear la pantalla completa de carga y
 * abortaba requests en vuelo — entre ellas la del logo, que se sirve con
 * `no-cache` y por eso se re-pide en cada montaje.
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status, user } = useAuth()
  const location = useLocation()

  if (status === 'hydrating' && user) {
    return <>{children}</>
  }
  if (status === 'idle' || status === 'hydrating') {
    return <FullPageHydrating />
  }
  if (status === 'anonymous') {
    const next = `${location.pathname}${location.search}`
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />
  }
  return <>{children}</>
}

function FullPageHydrating() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-lg bg-gradient-to-b from-surface to-surface-elevated px-lg text-center">
      <VerSportsLogo size="lg" />
      <div className="font-mono text-xs uppercase tracking-[0.18em] text-text-muted">
        Cargando
      </div>
    </div>
  )
}
